import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/App";
import { Castle, Building2, Phone, Mail, MapPin, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { fetchUser, organizations } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(organizations?.length > 0 ? "choose" : "create"); // choose or create
  
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
  });

  const handleCreateOrganization = async () => {
    if (!formData.name.trim()) {
      toast.error("Le nom de l'entreprise est requis");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/organizations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Erreur lors de la création");
      }

      await fetchUser();
      toast.success("Organisation créée avec succès !");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrganization = async (orgId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/organizations/switch/${orgId}`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) throw new Error("Erreur");

      await fetchUser();
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error("Erreur lors de la sélection");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen festive-gradient confetti-pattern flex items-center justify-center p-4">
      <Card className="w-full max-w-lg card-festive animate-fade-in">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-festive-pink to-festive-purple flex items-center justify-center shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-heading">
            {step === "choose" ? "Sélectionnez une organisation" : "Créez votre espace"}
          </CardTitle>
          <CardDescription>
            {step === "choose" 
              ? "Rejoignez une organisation existante ou créez-en une nouvelle"
              : "Configurez votre entreprise de location"
            }
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Choose existing organization */}
          {step === "choose" && organizations?.length > 0 && (
            <>
              <div className="space-y-3">
                {organizations.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => handleSelectOrganization(org.id)}
                    disabled={loading}
                    className="w-full p-4 rounded-2xl border-2 border-gray-100 hover:border-festive-pink hover:bg-pink-50 transition-colors text-left flex items-center gap-4"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-festive-pink/20 to-festive-purple/20 flex items-center justify-center">
                      <Castle className="w-6 h-6 text-festive-pink" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{org.name}</p>
                      <p className="text-sm text-gray-500 capitalize">{org.role}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </button>
                ))}
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">ou</span>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={() => setStep("create")}
                className="w-full h-12 rounded-xl border-2 border-dashed border-festive-pink text-festive-pink hover:bg-pink-50"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Créer une nouvelle organisation
              </Button>
            </>
          )}

          {/* Create new organization */}
          {step === "create" && (
            <>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-festive-pink" />
                    Nom de l'entreprise *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-festive mt-1"
                    placeholder="Ex: Mon Entreprise"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-festive-pink" />
                    Téléphone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-festive mt-1"
                    placeholder="0692 XX XX XX"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-festive-pink" />
                    Email de contact
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-festive mt-1"
                    placeholder="contact@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="address" className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-festive-pink" />
                    Adresse
                  </Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="input-festive mt-1"
                    placeholder="Adresse complète"
                    rows={2}
                  />
                </div>
              </div>

              <Button
                onClick={handleCreateOrganization}
                disabled={loading || !formData.name.trim()}
                className="w-full h-14 bg-gradient-to-r from-festive-pink to-festive-purple hover:opacity-90 text-white font-semibold text-lg rounded-2xl"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Créer mon espace
                  </>
                )}
              </Button>

              {organizations?.length > 0 && (
                <Button
                  variant="ghost"
                  onClick={() => setStep("choose")}
                  className="w-full text-gray-500"
                >
                  Retour à la sélection
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
